import type { ReactNode } from 'react'
import { CloudOff, Plus } from 'lucide-react'

import { cn } from '@/lib/tailwind.utils'
import { Button } from '@/components/ui/button'

export interface EmptyStateProps {
  icon?: ReactNode
  title: string
  description?: string
  actionLabel?: string
  onAction?: () => void
  maxWidth?: number | string
  showButton?: boolean
  buttonIcon?: ReactNode
  borderColor?: 'primary' | 'secondary' | 'neutral'
  className?: string
  action?: ReactNode
}

const borderColorClasses: Record<NonNullable<EmptyStateProps['borderColor']>, string> = {
  primary: 'border-primary',
  secondary: 'border-secondary',
  neutral: 'border-border',
}

export function EmptyState({
  icon = <CloudOff className="size-8" />,
  title,
  description,
  actionLabel,
  onAction,
  maxWidth,
  showButton = true,
  buttonIcon = <Plus className="size-4" />,
  borderColor = undefined,
  className,
  action,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'm-6 flex flex-1 flex-col items-center justify-center gap-4',
        'rounded-xl border border-dashed px-8 py-12 text-center',
        borderColor ? borderColorClasses[borderColor] : 'border-transparent',
        className,
      )}
      style={{ maxWidth }}
    >
      <div className="inline-flex items-center justify-center rounded-xl bg-muted p-3 text-foreground">
        {icon}
      </div>

      <h3 className="m-0 text-lg font-semibold text-foreground">{title}</h3>

      {description && <p className="m-0 max-w-[380px] text-sm text-foreground">{description}</p>}

      {action && action}

      {!action && actionLabel && onAction && showButton && (
        <Button size="sm" onClick={onAction}>
          {buttonIcon}
          {actionLabel}
        </Button>
      )}
    </div>
  )
}
